import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { ConfirmationModal } from 'web-ui-components/lib/organisms/modals';
import content, { contentPropTypes } from './CancelPayment.content';
import withContent from '../../../containers/withContent';
import {
	MANDATE_TYPE_SCHEDULE_PAYMENT,
	MANDATE_TYPE_STANDING_ORDER,
	MANDATE_TYPE_DIRECT_DEBIT,
	MANDATE_TYPE_BACS_CREDIT,
	MANDATE_TYPE_BACS_DEBIT,
	MANDATE_TYPE_SCHEDULE_TRANSFER,
	MANDATE_TYPE_IFT,
	MANDATE_TYPE_CHAPS,
} from '../../../config';
import {
	getCancelModalName,
	isCancelApiInProgress,
	getMandateToCancel,
	getCancelApiError,
} from '../../../states/cancelPayment/selectors';
import { handleCancelMandate, handleHideCancelModal } from '../../../states/cancelPayment/actions';
import { cancelModalFailure, cancelModalView, cancelModalSuccess } from '../../../states/cancelPayment/reducer';
import ScheduledPaymentDetails from '../PaymentDetails/ScheduledPaymentDetails';
import CancelPaymentLayout from './CancelPaymentLayout';
import StandingOrderDetails from '../PaymentDetails/StandingOrderDetails';
import DirectDebitDetails from '../PaymentDetails/DirectDebitDetails';
import BacsCreditPaymentDetails from '../PaymentDetails/BacsCreditPaymentDetails';
import BacsDebitPaymentDetails from '../PaymentDetails/BacsDebitPaymentDetails';
import CancelSuccessModal from '../CancelSuccessModal/CancelSuccessModal';
import CancelFailureModal from '../CancelFailureModal/CancelFailureModal';
import IFTDetails from '../PaymentDetails/IFTDetails';
import ChapsDetails from '../PaymentDetails/ChapsDetails';

const mapDispatch = (dispatch) => ({
	handleCancel: (mandate) => dispatch(handleCancelMandate(mandate)),
	handleCloseCancelModal: () => dispatch(handleHideCancelModal()),
});
const mapState = (state) => ({
	mandate: getMandateToCancel(state),
	cancelModalName: getCancelModalName(state),
	cancelApiError: getCancelApiError(state),
	isCancelApiInProgress: isCancelApiInProgress(state),
});

const CancelPaymentModal = ({
	content,
	mandate,
	handleCancel,
	isCancelApiInProgress,
	cancelModalName,
	handleCloseCancelModal,
	handleCancelSuccess,
	cancelApiError: { message: cancelErrorMsg = '' } = {},
}) => {
	const handleCancelSuccessOk = () => {
		handleCloseCancelModal();
		if (handleCancelSuccess) {
			handleCancelSuccess();
		}
	};
	const renderDetails = () => {
		const {
			cancelBacsPaymentSubHeader,
			cancelSchedulePaymentSubHeader,
			cancelStandingOrderSubHeader,
			cancelDirectDebitSubHeader,
			cancelIFTPaymentSubHeader,
			cancelBacsPaymentAlert,
			cancelDirectDebitAlert,
			cancelPaymentAlert,
			cancelIFTPaymentAlert,
			cancelCHAPSPaymentAlert,
		} = content;
		switch (mandate.mandateType) {
			case MANDATE_TYPE_SCHEDULE_PAYMENT:
			case MANDATE_TYPE_SCHEDULE_TRANSFER:
				return (
					<CancelPaymentLayout
						cancelPaymentAlert={cancelPaymentAlert}
						cancelPaymentSubHeader={cancelSchedulePaymentSubHeader}
					>
						<ScheduledPaymentDetails mandate={mandate} />
					</CancelPaymentLayout>
				);
			case MANDATE_TYPE_STANDING_ORDER:
				return (
					<CancelPaymentLayout
						cancelPaymentAlert={cancelPaymentAlert}
						cancelPaymentSubHeader={cancelStandingOrderSubHeader}
					>
						<StandingOrderDetails mandate={mandate} />
					</CancelPaymentLayout>
				);
			case MANDATE_TYPE_DIRECT_DEBIT:
				return (
					<CancelPaymentLayout
						cancelPaymentAlert={cancelDirectDebitAlert}
						cancelPaymentSubHeader={cancelDirectDebitSubHeader}
					>
						<DirectDebitDetails mandate={mandate} />
					</CancelPaymentLayout>
				);
			case MANDATE_TYPE_BACS_CREDIT:
				return (
					<CancelPaymentLayout
						cancelPaymentAlert={cancelBacsPaymentAlert}
						cancelPaymentSubHeader={cancelBacsPaymentSubHeader}
					>
						<BacsCreditPaymentDetails mandate={mandate} />
					</CancelPaymentLayout>
				);
			case MANDATE_TYPE_BACS_DEBIT:
				return (
					<CancelPaymentLayout
						cancelPaymentAlert={cancelBacsPaymentAlert}
						cancelPaymentSubHeader={cancelBacsPaymentSubHeader}
					>
						<BacsDebitPaymentDetails mandate={mandate} />
					</CancelPaymentLayout>
				);
			case MANDATE_TYPE_IFT:
				return (
					<CancelPaymentLayout
						cancelPaymentAlert={cancelIFTPaymentAlert}
						cancelPaymentSubHeader={cancelIFTPaymentSubHeader}
					>
						<IFTDetails mandate={mandate} />
					</CancelPaymentLayout>
				);
			case MANDATE_TYPE_CHAPS:
				return (
					<CancelPaymentLayout cancelPaymentAlert={cancelCHAPSPaymentAlert}>
						<ChapsDetails mandate={mandate} />
					</CancelPaymentLayout>
				);
			default:
				return null;
		}
	};
	const getCancelModalLabels = () => {
		const {
			cancelDDButtonLabel,
			cancelSOButtonLabel,
			cancelSinglePaymentLabel,
			cancelBacsPaymentLabel,
			cancelSingleTransferLabel,
			cancelIFTPaymentLabel,
			cancelChapsPaymentLabel,
			cancelDirectDebitHeader,
			cancelSchedulePaymentHeader,
			cancelStandingOrderHeader,
			cancelBacsPaymentHeader,
			cancelIFTPaymentHeader,
			cancelCHAPSPaymentHeader,
		} = content;
		const { mandateType } = mandate;
		const cancelModalLabels = {
			[MANDATE_TYPE_DIRECT_DEBIT]: { buttonLabel: cancelDDButtonLabel, header: cancelDirectDebitHeader },
			[MANDATE_TYPE_BACS_CREDIT]: { buttonLabel: cancelBacsPaymentLabel, header: cancelBacsPaymentHeader },
			[MANDATE_TYPE_BACS_DEBIT]: { buttonLabel: cancelBacsPaymentLabel, header: cancelBacsPaymentHeader },
			[MANDATE_TYPE_STANDING_ORDER]: { buttonLabel: cancelSOButtonLabel, header: cancelStandingOrderHeader },
			[MANDATE_TYPE_SCHEDULE_PAYMENT]: { buttonLabel: cancelSinglePaymentLabel, header: cancelSchedulePaymentHeader },
			[MANDATE_TYPE_SCHEDULE_TRANSFER]: { buttonLabel: cancelSingleTransferLabel, header: cancelSchedulePaymentHeader },
			[MANDATE_TYPE_IFT]: { buttonLabel: cancelIFTPaymentLabel, header: cancelIFTPaymentHeader },
			[MANDATE_TYPE_CHAPS]: { buttonLabel: cancelChapsPaymentLabel, header: cancelCHAPSPaymentHeader },
		}[mandateType];
		return cancelModalLabels;
	};

	const labels = getCancelModalLabels();

	const renderCancelView = () => (
		<ConfirmationModal
			isOpen
			titleText={labels.header}
			dismissButtonText={content.backButtonLabel}
			confirmButtonText={labels.buttonLabel}
			confirmButtonIsDisabled={isCancelApiInProgress}
			onConfirm={() => handleCancel(mandate)}
			onClose={handleCloseCancelModal}
		>
			{renderDetails()}
		</ConfirmationModal>
	);

	const renderCancelFailure = () => (
		<CancelFailureModal
			failureHeader={cancelErrorMsg}
			handleClickOk={handleCloseCancelModal}
			okButtonLabel={content.labelOk}
		/>
	);
	const renderCancelSuccess = () => <CancelSuccessModal mandate={mandate} handleClickOk={handleCancelSuccessOk} />;

	switch (cancelModalName) {
		case cancelModalView:
			return renderCancelView();
		case cancelModalFailure:
			return renderCancelFailure();
		case cancelModalSuccess:
			return renderCancelSuccess();
		default:
			return null;
	}
};

CancelPaymentModal.propTypes = {
	content: PropTypes.shape(contentPropTypes),
	mandate: PropTypes.shape({}),
	handleCloseCancelModal: PropTypes.func.isRequired,
	handleCancel: PropTypes.func.isRequired,
	isCancelApiInProgress: PropTypes.bool.isRequired,
	cancelModalName: PropTypes.string.isRequired,
	cancelApiError: PropTypes.shape({
		message: PropTypes.string.isRequired,
	}),
	handleCancelSuccess: PropTypes.func.isRequired,
};
const contentWrapper = withContent({ content })(CancelPaymentModal);
export default connect(mapState, mapDispatch)(contentWrapper);
